import { AlertOptions } from 'react-alert';
import { NavLinkProps } from 'react-router';
import { SerializedError } from '@reduxjs/toolkit';
import { QueryStatus } from '@reduxjs/toolkit/query';
import { DataFieldRenderFormField } from '@toolpad/core';
import { RefObject, ReactNode, PropsWithChildren } from 'react';

import { Form } from './forms';
import { ReportsData, ReportConfig, ReportConfigWithRoles } from './reports';
import {
  EnrollmentType,
  CourseReportType,
  TenantReportType,
  TranslationState,
  VerificationType,
  ResourceItemType,
  ProjectReportType,
  ReportTemplateType,
  PdfReportLogAction,
  CourseSubmissionStatus,
} from './enums';

export * from './enums';
export * from './forms';
export * from './reports';

export type ChildrenProps<T = unknown> = PropsWithChildren<T>;

export type UnknownObject = Record<string, unknown>;

export type Nullable<T> = T | null | undefined;

export interface RouterNavLinkProps extends NavLinkProps {
  id: string;
  label: string;
  disabled?: boolean;
  badgeCount?: number;
}

export interface LocationState {
  backPath?: NavLinkProps['to'];
}

export interface ForwardRefType<T = unknown> {
  refForward?: (element: T | null) => RefObject<T> | void | null | undefined;
}

export interface IdObject<T = string> {
  id: T;
}

export interface BasePageProps {
  isError: boolean;
  error?: ReactNode;
  refetch: () => void;
  isLoading?: boolean;
}

export interface UploadFile {
  src: any;
  file: File;
}

export interface DateRange {
  to?: Date | null;
  from?: Date | null;
}

export interface UseQueryReturnProps<T = unknown> {
  data?: T;
  isError: boolean;
  isFetching: boolean;
  refetch: () => void;
  isUninitialized?: boolean;
  error?: Error | SerializedError;
}

export interface SafeRefetchProps {
  isLoading: boolean;
  refetch: () => void;
  isUninitialized: boolean;
}

export interface AuthEmail {
  email: string;
  password: string;
}

export interface BaseResponse<T> {
  data: T;
  message: any;
  success: boolean;
  totaltime_pages: number;
  permission_updated: boolean;
}

export interface PaginationRequest {
  page: number;
  limit?: number;
}

export interface BaseHandleMutationProps {
  error?: any;
  reset?: () => void;
  status?: QueryStatus;
  onSuccess?: () => void;
  showError: (error: string) => void;
  onError?: (errorMessage: any, error: any) => void;
}

export type HandleMutationProps = Omit<BaseHandleMutationProps, 'showError'>;

export interface AlertOptionsType extends AlertOptions {
  title?: string;
  icon?: ReactNode;
}

export type DataFieldRenderFormFieldWithLabel = (
  params: Parameters<DataFieldRenderFormField>[0] & {
    label?: string;
  }
) => ReturnType<DataFieldRenderFormField>;

export const NumPads = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '*',
  '0',
  '#',
] as const;
export type NumPadKey = (typeof NumPads)[number];

export enum FileType {
  File = 'file',
  Image = 'image',
  Video = 'video',
}

export interface Media {
  id: number;
  url: string;
  name: string;
  size?: number;
  type: FileType;
  thumbnailUrl?: Nullable<string>;
}

export type TimeZone = {
  id: number;
  name: string;
  code: string;
  time_zone_code: string;
};

export interface AuthEmail {
  email: string;
  password: string;
}

export interface TenantTranslations {
  landingText?: string;
  childEnteringTypes: string[];
}

export interface Tenant {
  id: string;
  name: string;
  zip?: string;
  city?: string;
  state?: string;
  siteUrl: string;
  county?: string;
  address?: string;
  projectId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  siteLogo?: string;
  publicName: string;
  code?: string | null;
  landingText?: string;
  contactEmail: string;
  logo?: string | null;
  languages?: string[];
  translatedAt?: string;
  mcirIdEnabled?: boolean;
  ssoUserEnabled?: boolean;
  fullCode?: string | null;
  libraryEnabled?: boolean;
  ssoAdminEnabled?: boolean;
  privacyPolicyUrl?: string;
  gameVideoUrl?: string | null;
  childEnteringTypes: string[];
  gameConfigUrl?: string | null;
  ssoIdentifier?: string | null;
  reportsData?: TenantReportsData;
  libraryPublicApiEnabled?: boolean;
  translations?: TenantTranslations;
  translationFailureReason?: string;
  translationState: TranslationState;
  auth0SamlConnection?: string | null;
  adminTerminology?: AdminTerminology;
  participantManagementEnabled?: boolean;
  guestLoginRequiresExistingUser?: boolean;
  userTableColumnsByRole?: UserTableColumnsByRole;
}

export interface LandingCta {
  label?: string;
}

export interface LandingGameUrls {
  webUrl?: string;
  iosUrl?: string;
  androidUrl?: string;
  tryGameUrl?: string;
}

export interface ProjectSiteConfig {
  landing?: {
    game?: LandingGameUrls;
    cta?: {
      primary?: LandingCta;
      secondary?: LandingCta;
    };
    hero?: {
      title?: string;
      subtitle?: string;
      highlight?: string;
    };
    about?: {
      title?: string;
      videoUrl?: string;
      description?: string;
    };
    discover?: {
      title?: string;
      subtitle?: string;
      highlight?: string;
    };
  };
}

export interface PublicSignupOption {
  label: string;
  tenantId: string;
}

export interface PublicSignupConfig {
  allTenantId: string | null;
  options: PublicSignupOption[];
}

export interface AdminTerminology {
  client?: { plural: string; singular: string };
}

export interface Project {
  id: string;
  name: string;
  createdAt: Date;
  updatedAt: Date;
  publicName: string;
  senderEmail: string;
  senderName?: string;
  logo?: string | null;
  isZipRequired: boolean;
  isPhoneRequired: boolean;
  auth0Config: Auth0Config;
  contactUsTemplate: string;
  isBirthdateRequired: boolean;
  siteConfig?: ProjectSiteConfig;
  publicTenantListEnabled: boolean;
  reportsData?: ProjectReportsData;
  coursePdfTemplate?: string | null;
  usersMfaOptions: VerificationType[];
  adminTerminology?: AdminTerminology;
  coursePdfPreviewUrl?: string | null;
  allowQuestionCodesAsHeaders?: boolean;
  participantManagementEnabled?: boolean;
  publicSignupConfig?: PublicSignupConfig;
  guestLoginRequiresExistingUser?: boolean;
  userTableColumnsByRole?: UserTableColumnsByRole;
  coursePdfFile?: { key: string; filename: string } | null;
  coursePdfPreview?: { key: string; filename: string } | null;
}

export interface HealthDepartmentOption {
  healthDepartmentId: string;
  healthDepartmentName: string;
}

export interface User {
  id: string;
  role: Role;
  phone?: string;
  email?: string;
  auth0Id: string;
  isDemo: boolean;
  guestId?: string;
  lastName?: string;
  fullName?: string;
  tenantId?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  firstName?: string;
  tenantIds?: string[];
  projectIds?: string[];
  isCourseCompleted: boolean;
  isLibraryAccessEnabled: boolean;
  healthDepartmentScopes?: HealthDepartmentOption[];
  projects?: Pick<Project, 'id' | 'name' | 'logo'>[];
  tenants?: Pick<Tenant, 'id' | 'name' | 'logo' | 'siteLogo'>[];
  tenant?: Pick<
    Tenant,
    | 'id'
    | 'name'
    | 'logo'
    | 'siteLogo'
    | 'languages'
    | 'projectId'
    | 'reportsData'
    | 'translations'
    | 'mcirIdEnabled'
    | 'libraryEnabled'
    | 'childEnteringTypes'
  >;
}

export interface Pagination {
  limit: number;
  offset: number;
}

export type PaginationOptional = Partial<Pagination>;

export interface SearchablePagination extends Partial<Pagination> {
  search?: string;
}

export interface PaginationResponse<T> {
  data: T[];
  total: number;
}

export enum UserAuditEventType {
  USER_CREATED_BY_ADMIN = 'user_created_by_admin',
  SIGNUP_COMPLETED = 'signup_completed',
  LOGIN_STARTED = 'login_started',
  LOGIN_SUCCESS = 'login_success',
  LOGIN_FAILED = 'login_failed',
  TOKEN_REFRESH_SUCCESS = 'token_refresh_success',
  TOKEN_REFRESH_FAILED = 'token_refresh_failed',
  LOGOUT_SUCCESS = 'logout_success',
  LOGOUT_FAILED = 'logout_failed',
  PASSWORD_RESET_REQUESTED = 'password_reset_requested',
  MFA_CHALLENGE_STARTED = 'mfa_challenge_started',
  MFA_ENROLL_STARTED = 'mfa_enroll_started',
  MFA_VERIFY_SUCCESS = 'mfa_verify_success',
  MFA_VERIFY_FAILED = 'mfa_verify_failed',
  MFA_RESET_BY_ADMIN = 'mfa_reset_by_admin',
  EMAIL_VERIFICATION_SENT = 'email_verification_sent',
  EMAIL_VERIFICATION_CONFIRMED = 'email_verification_confirmed',
  TENANT_SWITCHED = 'tenant_switched',
  PROFILE_UPDATED = 'profile_updated',
  PROFILE_FORM_SUBMITTED = 'profile_form_submitted',
  USER_STATUS_CHANGED = 'user_status_changed',
  USER_ROLE_CHANGED = 'user_role_changed',
  USER_DELETED = 'user_deleted',
  USER_RESTORED = 'user_restored',
  SESSION_STARTED = 'session_started',
  SESSION_TIMEOUT = 'session_timeout',
  CHILD_PII_READ = 'child_pii_read',
}

export type AuditEventUser = Pick<
  User,
  'id' | 'role' | 'email' | 'guestId' | 'lastName' | 'firstName'
>;

export interface UserAuditEvent {
  id: string;
  occurredAt: string;
  tenantId: string | null;
  type: UserAuditEventType;
  sessionId: string | null;
  actorUserId: string | null;
  targetUserId: string | null;
  actor?: AuditEventUser | null;
  targetIdentifier: string | null;
  metadata: Record<string, unknown>;
  targetUser?: AuditEventUser | null;
  tenant?: Pick<Tenant, 'id' | 'name'> | null;
}

export interface ProjectAdminConfig {
  adminTerminology: AdminTerminology;
  allowQuestionCodesAsHeaders?: boolean;
  userTableColumnsByRole: UserTableColumnsByRole;
}

export type UserTableColumnRole = 'admin' | 'tenant_admin' | 'project_admin';
export type UserTableColumnField =
  | 'role'
  | 'email'
  | 'isDemo'
  | 'guestId'
  | 'lastName'
  | 'tenantId'
  | 'isActive'
  | 'firstName'
  | 'createdAt'
  | 'updatedAt'
  | 'isCourseCompleted';
export type UserTableColumnsByRole = Partial<
  Record<UserTableColumnRole, UserTableColumnField[]>
>;

export enum Role {
  ADMIN = 'admin',
  PROJECT_ADMIN = 'project_admin',
  RESEARCH_ADMIN = 'research_admin',
  REPORT_MANAGER = 'report_manager',
  TENANT_ADMIN = 'tenant_admin',
  TENANT_USER = 'tenant_user',
  TENANT_GUEST = 'tenant_guest',
  CONTENT_MANAGER = 'content_manager',
  LIBRARY_ADMIN = 'library_admin',
  LHD_MIVAX_ADMIN = 'lhd_mivax_admin',
}

export const UserRoleTitle = Object.freeze({
  [Role.ADMIN]: 'Admin',
  [Role.LIBRARY_ADMIN]: 'Library Admin',
  [Role.PROJECT_ADMIN]: 'Project Admin',
  [Role.REPORT_MANAGER]: 'Report Manager',
  [Role.TENANT_USER]: 'Organization User',
  [Role.RESEARCH_ADMIN]: 'Research Admin',
  [Role.LHD_MIVAX_ADMIN]: 'LHD MIVAX Admin',
  [Role.CONTENT_MANAGER]: 'Content Manager',
  [Role.TENANT_ADMIN]: 'Organization Admin',
  [Role.TENANT_GUEST]: 'Organization Client',
});

export const VerificationTypeTitle = Object.freeze({
  [VerificationType.OTP]: 'OTP',
  [VerificationType.EMAIL]: 'Email',
  [VerificationType.PHONE]: 'Phone',
});

export interface BasicResourceInfo {
  url: string;
  text: string;
  name: string;
}

export interface BasicResourceStep extends IdObject {
  title: string;
  items: BasicResourceInfo[];
}

export interface Course {
  id: string;
  name: string;
  version: number;
  stepIds: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  languages?: string[];
  isPublished: boolean;
  translatedAt?: string;
  tenantId?: string | null;
  projectId?: string | null;
  reportsData?: CourseReportsData;
  translationFailureReason?: string;
  isOutcomePipelineEnabled?: boolean;
  translationState: TranslationState;
  tenant?: Pick<Tenant, 'id' | 'name'>;
  project?: Pick<Project, 'id' | 'name'>;
  courseCompletionMessage?: string | null;
  courseCompletionButtonUrl?: string | null;
  courseCompletionButtonTitle?: string | null;
  translations?: Record<
    string,
    {
      courseCompletionMessage?: string;
      courseCompletionButtonTitle?: string;
    }
  >;
}

export interface CourseStep {
  id: string;
  name: string;
  color: string;
  courseId: string;
  codeName: string;
  createdAt: string;
  updatedAt: string;
  coverImage: string;
  formVersions: Form[];
  resourceVersions: Resource[];
  translations?: Record<string, { name?: string }>;
  course: Pick<Course, 'name' | 'tenant' | 'tenantId' | 'projectId'>;
}

export interface BasicResourceItem {
  url?: string;
  name?: string;
  text?: string;
  volume?: number;
  showPlayBar?: boolean;
  type: ResourceItemType;
}

export interface ResourceBlockTranslation {
  text?: string;
  name?: string;
  items?: Pick<BasicResourceItem, 'name' | 'text' | 'type'>[];
}

export interface ResourceBlock {
  id: string;
  name: string;
  text: string;
  video: string;
  color?: string;
  volume: number;
  duration: number;
  showPlayBar: boolean;
  items: BasicResourceItem[];
  translations?: Record<string, ResourceBlockTranslation>;
}

export interface ResourceData {
  blocks: ResourceBlock[];
}

export interface Resource {
  id: string;
  version: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  data: ResourceData;
  courseStepId: string;
  tenantId?: string | null;
}

export interface LibraryTag {
  id: string;
  name: string;
  count: number;
  isGroup: boolean;
  sortOrder: number;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  parentTagId?: string | null;
}

export interface LibraryTagTreeItem extends LibraryTag {
  children: LibraryTagTreeItem[];
}

export interface LibraryTagCompact extends Pick<LibraryTag, 'id' | 'name'> {
  parentTag?: LibraryTagCompact;
}

export interface LibraryResourceFile {
  id: string;
  mimeType: string;
  createdAt: string;
  updatedAt: string;
  resourceId: string;
  storagePath: string;
  fileSizeBytes: string;
  originalFileName: string;
  previewUrl: string | null;
  widthInches: number | null;
  widthPixels: number | null;
  heightInches: number | null;
  heightPixels: number | null;
  resolutionPpi: number | null;
}

export interface LibraryResource {
  id: string;
  title: string;
  tagIds: string[];
  createdAt: string;
  updatedAt: string;
  description: string;
  deletedAt: string | null;
  file: LibraryResourceFile;
}

export interface Child {
  id: string;
  grade: string;
  userId: string;
  lastName: string;
  firstName: string;
  createdAt: string;
  updatedAt: string;
  dateOfBirth: string;
  mcirId?: string | null;
  childEnteringType: string;
  childcareSchoolName: string;
  vaccinesOfConcern: string[];
  usesDelayedSchedule: boolean;
  nonMedicalExemptionType: string;
  otherExemptionTypeDescription?: string;
  isFirstTimeNonMedicalExemption: boolean;
}

export interface SubmissionCourse extends Pick<
  Course,
  'name' | 'tenantId' | 'projectId'
> {
  tenant?: Pick<Tenant, 'name'>;
}

export interface CourseEnrollment {
  id: string;
  courseId: string;
  createdAt: string;
  updatedAt: string;
  children: Child[];
  enrollmentIds: string[];
  enrollmentType: EnrollmentType;
}

export interface CourseSubmission {
  id: string;
  userId: string;
  courseId: string;
  createdAt: string;
  updatedAt: string;
  finishedAt?: string;
  isCompleted: boolean;
  courseVersion: number;
  course?: SubmissionCourse;
  status: CourseSubmissionStatus;
  courseEnrollment?: CourseEnrollment;
}

export type TenantReportConfigItem =
  TenantReportConfig | TenantReportConfigWithRoles;

export type TenantReportsData = ReportsData<TenantReportConfigItem>;

export interface TenantReportConfig extends ReportConfig<TenantReportType> {}

export interface TenantReportConfigWithRoles extends ReportConfigWithRoles<
  TenantReportType.USERS | TenantReportType.PROFILE_FORMS,
  Role.TENANT_USER | Role.TENANT_GUEST
> {}

export const TenantReportTypeTitle = Object.freeze({
  [TenantReportType.USERS]: 'Users',
  [TenantReportType.CHILDREN]: 'Children',
  [TenantReportType.PROFILE_FORMS]: 'Profile Forms',
});

export type ProjectReportConfigItem =
  ProjectReportConfig | ProjectReportConfigWithRoles;

export type ProjectReportsData = ReportsData<ProjectReportConfigItem>;

export interface ProjectReportConfig extends ReportConfig<ProjectReportType> {}

export interface ProjectReportConfigWithRoles extends ReportConfigWithRoles<
  ProjectReportType.USERS,
  Role.TENANT_USER | Role.TENANT_GUEST
> {}

export const ProjectReportTypeTitle = Object.freeze({
  [ProjectReportType.USERS]: 'Users',
  [ProjectReportType.CHILDREN]: 'Children',
  [ProjectReportType.COMBINED_USERS]: 'Combined Users',
  [ProjectReportType.COMBINED_CHILDREN]: 'Combined Children',
  [ProjectReportType.COMBINED_COURSE_SESSIONS]: 'Combined Course Sessions',
});

export interface CourseReportsData {
  configs: (CourseReportConfig | CourseStepsReportConfig)[];
}

export interface CourseReportConfig {
  id: string;
  name: string;
  type: CourseReportType;
  onlyForAdmins: boolean;
}

export interface CourseStepsReportConfig extends CourseReportConfig {
  stepIds?: string[];
  sectionIndex?: number;
  includeActive: boolean;
  includeActivity: boolean;
  includeResponses: boolean;
  type: CourseReportType.STEPS;
  responseIndexesAsValues: boolean;
  useQuestionCodesAsHeaders?: boolean;
  includeProfileFormResponses?: boolean;
  includeCertificateDownloadTime: boolean;
  renderAnswerCodesAsBinaryColumns?: boolean;
}

export const CourseReportTypeTitle = Object.freeze({
  [CourseReportType.STEPS]: 'Steps',
});

export interface ReportData extends PaginationResponse<string[]> {
  headers: string[];
}

export interface UsedVersions {
  usedVersions: number[];
}

export interface Auth0Config {
  domain: string;
  clientId: string;
  connection: string;
  clientSecret: string;
}

export interface MfaEnrollResponse {
  secret?: string;
  oob_code?: string;
  oob_channel?: string;
  barcode_uri?: string;
  binding_method?: string;
  recovery_codes?: string[];
  authenticator_type: string;
}

export interface MfaChallengeResponse {
  oob_code: string;
  binding_method: string;
  challenge_type: string;
}

export interface VerifyCode {
  code: string;
  oobCode?: string;
  type: VerificationType;
}

export enum ChildCourseSubmissionStatus {
  INCLUDED = 'included',
  NOT_INCLUDED = 'not_included',
  NO_COURSE_ENROLLMENT = 'no_course_enrollment',
}

export const ChildCourseSubmissionStatusTitle = Object.freeze({
  [ChildCourseSubmissionStatus.INCLUDED]: 'Included',
  [ChildCourseSubmissionStatus.NO_COURSE_ENROLLMENT]: 'None',
  [ChildCourseSubmissionStatus.NOT_INCLUDED]: 'Not included',
});

export interface ReportTemplate {
  id: string;
  name: string;
  version: number;
  reportId: string;
  courseId?: string;
  projectId?: string;
  tenantId?: string | null;
  type: ReportTemplateType;
  template: Record<string, unknown>;
}

export const ADMIN_LIKE_ROLES: Role[] = [
  Role.ADMIN,
  Role.PROJECT_ADMIN,
] as const;

export interface PdfReportLog {
  id: string;
  userId: string;
  tenantId: string;
  createdAt: string;
  occurredAt: string;
  pdfReportId: string;
  actorId: string | null;
  action: PdfReportLogAction;
  actor: Pick<User, 'id' | 'lastName' | 'firstName'> | null;
  pdfReport: Pick<PdfReport, 'fileSizeBytes' | 'originalFileName'> | null;
}

export interface PdfReport {
  id: string;
  userId: string;
  tenantId: string;
  mimeType: string;
  createdAt: string;
  updatedAt: string;
  createdById: string;
  fileSizeBytes: number;
  originalFileName: string;
  deletedAt: string | null;
  deletedById: string | null;
  tenant: Pick<Tenant, 'id' | 'name'> | null;
  createdBy: Pick<User, 'id' | 'lastName' | 'firstName'> | null;
  deletedBy: Pick<User, 'id' | 'lastName' | 'firstName'> | null;
}
